import { useEffect, useState } from 'react';

// What a take would send to Google, asked of the server (/api/tts-preview):
// it runs the request body through the very steps a take goes through — the
// glossary, the split into turns, the voices of each request — so a preview
// can't show something the voicing doesn't do. `body` is what buildTtsRequest
// makes; null asks nothing. Asked again 300 ms after the body last changed.
// -> { data: { format, scheme, systemPrompt, requests: [{ turn, speaker, slots, text, parts }] } | null, error, loading }
// `format` is the request shape the model takes: 'prompt' (the prompt and
// the labeled transcript as one text, up to 3.1) or 'parts' (3.8: one text
// part per turn, each with its speaker and style; no prompt).
export function useTtsPreview(body) {
  const key = body ? JSON.stringify(body) : null;
  const [state, setState] = useState({ data: null, error: null, loading: false });
  useEffect(() => {
    if (!key) {
      setState({ data: null, error: null, loading: false });
      return undefined;
    }
    let dead = false;
    setState((s) => ({ ...s, error: null, loading: true }));
    const timer = setTimeout(() => {
      fetch('/api/tts-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: key,
      })
        .then(async (r) => {
          const d = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
          return d;
        })
        .then((d) => { if (!dead) setState({ data: d, error: null, loading: false }); })
        .catch((e) => { if (!dead) setState({ data: null, error: e.message || String(e), loading: false }); });
    }, 300);
    return () => { dead = true; clearTimeout(timer); };
  }, [key]);
  return state;
}

// The voices of one request: one name when the turn's voice fills both
// slots, both names when the model assigns them itself.
export const slotsLabel = (r) => (r.slots[0] === r.slots[1]
  ? r.slots[0]
  : `${r.slots[0]} / ${r.slots[1]}${r.parts ? '' : ' (the model picks)'}`);

// One request of a preview as text: its voices, then — in the 'parts'
// shape — each part with its speaker and style, or the prompt and the text.
export const requestPreviewText = (data, r) => {
  const many = data.requests.length > 1;
  const head = `━━ ${many ? `Request ${r.turn}` : 'One request'} · voices: ${slotsLabel(r)} ━━`;
  if (r.parts) {
    return `${head}\n${r.parts.map((p) => (
      `[${p.speaker}${p.style ? ` · style: ${p.style}` : ''}]\n${p.text}`
    )).join('\n\n')}`;
  }
  return `${head}\n${data.systemPrompt || ''}\n${r.text}`;
};
